import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Plus, Search, Wrench, FileText, Upload, Download, Filter, Edit, Trash2, Eye } from 'lucide-react';
import api from '../lib/api';
import { fmtDate } from '../lib/dateUtils';
import { useAuth } from '../contexts/AuthContext';

export default function EquipmentPage() {
  const navigate = useNavigate();
  const { isRole } = useAuth();
  const [items, setItems] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [equipDocs, setEquipDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showMaint, setShowMaint] = useState(false);
  const [showDocCreate, setShowDocCreate] = useState(false);
  const [showUpload, setShowUpload] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({ name: '', category: '', internal_code: '', serial_number: '', license_plate: '', brand: '', model: '', purchase_date: '', status: 'disponibile', current_location: '', notes: '' });
  const [editForm, setEditForm] = useState({ name: '', category: '', internal_code: '', serial_number: '', license_plate: '', brand: '', model: '', purchase_date: '', status: 'disponibile', current_location: '', notes: '' });
  const [maintForm, setMaintForm] = useState({ equipment_id: '', maintenance_date: '', maintenance_type: 'ordinaria', description: '', spare_parts: '', cost: 0, supplier: '', technician: '', next_due_date: '' });
  const [docForm, setDocForm] = useState({ equipment_id: '', doc_type: 'altro', title: '', reference_number: '', issuing_entity: '', issue_date: '', expiry_date: '', notes: '' });

  const load = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterCategory && filterCategory !== '__all__') params.category = filterCategory;
      
      const [eqRes, mtRes, catRes, docsRes] = await Promise.all([
        api.get('/equipment', { params }),
        api.get('/maintenance'),
        api.get('/categories/equipment').catch(() => ({ data: [] })),
        api.get('/equipment-documents').catch(() => ({ data: [] })),
      ]);
      setItems(eqRes.data);
      setMaintenance(mtRes.data);
      setCategories(catRes.data);
      setEquipDocs(docsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search, filterCategory]);

  const statusBadge = (status) => {
    const m = {
      disponibile: 'bg-green-50 text-green-700 border-green-200',
      assegnata: 'bg-blue-50 text-blue-700 border-blue-200',
      in_manutenzione: 'bg-amber-50 text-amber-700 border-amber-200',
      non_disponibile: 'bg-slate-100 text-slate-600 border-slate-200',
      fuori_uso: 'bg-red-50 text-red-700 border-red-200',
    };
    return <Badge variant="outline" className={m[status] || m.disponibile}>{status?.replace('_', ' ') || 'N/A'}</Badge>;
  };

  const handleCreate = async () => {
    await api.post('/equipment', form);
    setShowCreate(false);
    setForm({ name: '', category: '', internal_code: '', serial_number: '', license_plate: '', brand: '', model: '', purchase_date: '', status: 'disponibile', current_location: '', notes: '' });
    load();
  };

  const handleMaint = async () => {
    await api.post('/maintenance', { ...maintForm, cost: parseFloat(maintForm.cost) || 0 });
    setShowMaint(false);
    setMaintForm({ equipment_id: '', maintenance_date: '', maintenance_type: 'ordinaria', description: '', spare_parts: '', cost: 0, supplier: '', technician: '', next_due_date: '' });
    load();
  };

  const handleDocCreate = async () => {
    try {
      await api.post('/equipment-documents', docForm);
      setShowDocCreate(false);
      setDocForm({ equipment_id: '', doc_type: 'altro', title: '', reference_number: '', issuing_entity: '', issue_date: '', expiry_date: '', notes: '' });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleEditEquipment = (eq) => {
    setSelectedEquipment(eq);
    setEditForm({
      name: eq.name,
      category: eq.category || '',
      internal_code: eq.internal_code || '',
      serial_number: eq.serial_number || '',
      license_plate: eq.license_plate || '',
      brand: eq.brand || '',
      model: eq.model || '',
      purchase_date: eq.purchase_date || '',
      status: eq.status || 'disponibile',
      current_location: eq.current_location || '',
      notes: eq.notes || ''
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/equipment/${selectedEquipment.id}`, editForm);
      setShowEdit(false);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteEquipment = (eq) => {
    setSelectedEquipment(eq);
    setShowDelete(true);
  };

  const confirmDelete = async (permanent) => {
    try {
      await api.delete(`/equipment/${selectedEquipment.id}?permanent=${permanent}`);
      setShowDelete(false);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try {
      await api.delete(`/equipment-documents/${docId}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteMaint = async (maintId) => {
    if (!window.confirm('Eliminare questa manutenzione?')) return;
    try {
      await api.delete(`/maintenance/${maintId}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleUploadDoc = async (docId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/upload/equipment-doc/${docId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setShowUpload(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore upload');
    }
  };

  const DOC_TYPES = [
    { value: 'assicurazione', label: 'Assicurazione' },
    { value: 'verifica_annuale', label: 'Verifica Annuale' },
    { value: 'certificazione', label: 'Certificazione' },
    { value: 'revisione', label: 'Revisione' },
    { value: 'collaudo', label: 'Collaudo' },
    { value: 'libretto', label: 'Libretto' },
    { value: 'altro', label: 'Altro' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="equipment-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Attrezzature</h1>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 sm:w-48">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Cerca attrezzatura..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="equip-search" />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-40" data-testid="filter-category">
              <Filter size={14} className="mr-1" />
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutte</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isRole('admin', 'aspp', 'qhse') && (
            <>
              <Button onClick={() => setShowDocCreate(true)} variant="outline" data-testid="add-doc-btn"><FileText size={16} className="mr-1" /> Documento</Button>
              <Button onClick={() => setShowMaint(true)} variant="outline" data-testid="add-maint-btn"><Wrench size={16} className="mr-1" /> Manutenzione</Button>
              <Button onClick={() => setShowCreate(true)} data-testid="add-equip-btn"><Plus size={16} className="mr-1" /> Nuova</Button>
            </>
          )}
        </div>
      </div>

      <Tabs defaultValue="elenco">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="elenco">Elenco ({items.length})</TabsTrigger>
          <TabsTrigger value="documenti">Documenti ({equipDocs.length})</TabsTrigger>
          <TabsTrigger value="manutenzioni">Manutenzioni ({maintenance.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="elenco">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead className="hidden sm:table-cell">Codice</TableHead>
                    <TableHead className="hidden md:table-cell">Marca/Modello</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="hidden lg:table-cell">Posizione</TableHead>
                    <TableHead>Manut.</TableHead>
                    {isRole('admin', 'aspp') && <TableHead className="w-20">Azioni</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Nessuna attrezzatura</TableCell></TableRow>
                  ) : items.map(eq => (
                    <TableRow key={eq.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`equip-row-${eq.id}`} onClick={() => navigate(`/equipment/${eq.id}`)}>
                      <TableCell><div><p className="text-sm font-medium">{eq.name}</p><p className="text-xs text-slate-500">{eq.category}</p></div></TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-slate-600">{eq.internal_code || '-'}</TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-slate-600">{eq.brand} {eq.model}</TableCell>
                      <TableCell>{statusBadge(eq.status)}</TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-slate-600">{eq.current_location || '-'}</TableCell>
                      <TableCell>
                        {eq.maintenance_status === 'scaduto' ? <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Scaduta</Badge> :
                         eq.maintenance_status === 'in_scadenza' ? <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">In Scad.</Badge> :
                         <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">OK</Badge>}
                      </TableCell>
                      {isRole('admin', 'aspp') && (
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/equipment/${eq.id}`); }} title="Dettaglio">
                              <Eye size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleEditEquipment(eq); }} title="Modifica">
                              <Edit size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteEquipment(eq); }} className="text-red-600 hover:text-red-700" title="Elimina">
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documenti">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Attrezzatura</TableHead>
                    <TableHead>Documento</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Scadenza</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>File</TableHead>
                    {isRole('admin', 'aspp', 'qhse') && <TableHead className="w-20">Azioni</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {equipDocs.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Nessun documento</TableCell></TableRow>
                  ) : equipDocs.map(doc => (
                    <TableRow key={doc.id} className="hover:bg-slate-50">
                      <TableCell className="text-sm font-medium">{doc.equipment_name || 'N/A'}</TableCell>
                      <TableCell className="text-sm">{doc.title}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 text-xs">
                          {doc.doc_type?.replace('_', ' ') || 'altro'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{doc.expiry_date ? fmtDate(doc.expiry_date) : 'N/A'}</TableCell>
                      <TableCell>
                        {doc.status === 'scaduto' ? <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Scaduto</Badge> :
                         doc.status === 'in_scadenza' ? <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">In Scad.</Badge> :
                         <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Valido</Badge>}
                      </TableCell>
                      <TableCell>
                        {doc.file_url ? (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
                            <Download size={14} /> Scarica
                          </a>
                        ) : isRole('admin', 'aspp', 'qhse') ? (
                          <Button variant="ghost" size="sm" onClick={() => setShowUpload(doc.id)} className="text-xs p-1 h-auto">
                            <Upload size={14} className="mr-1" /> Carica
                          </Button>
                        ) : <span className="text-slate-400 text-xs">-</span>}
                      </TableCell>
                      {isRole('admin', 'aspp', 'qhse') && (
                        <TableCell>
                          <div className="flex gap-1">
                            {!doc.file_url && (
                              <Button variant="ghost" size="sm" onClick={() => setShowUpload(doc.id)} className="p-1 h-auto text-blue-600">
                                <Upload size={14} />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteDoc(doc.id)} className="p-1 h-auto text-red-600 hover:text-red-800">
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="manutenzioni">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Attrezzatura</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="hidden sm:table-cell">Descrizione</TableHead>
                    <TableHead className="hidden md:table-cell text-right">Costo</TableHead>
                    {isRole('admin', 'aspp') && <TableHead className="w-16">Azioni</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {maintenance.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Nessuna manutenzione</TableCell></TableRow>
                  ) : maintenance.map(m => (
                    <TableRow key={m.id} className="hover:bg-slate-50">
                      <TableCell className="text-sm font-medium">{m.equipment_name}</TableCell>
                      <TableCell className="text-sm">{fmtDate(m.maintenance_date)}</TableCell>
                      <TableCell><Badge variant="outline" className={m.maintenance_type === 'straordinaria' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-600 border-slate-200'}>{m.maintenance_type}</Badge></TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-slate-600 max-w-[200px] truncate">{m.description}</TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-right font-medium">{m.cost ? `${m.cost.toFixed(2)} EUR` : '-'}</TableCell>
                      {isRole('admin', 'aspp') && (
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteMaint(m.id)} className="p-1 h-auto text-red-600 hover:text-red-800">
                            <Trash2 size={14} />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Equipment Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuova Attrezzatura</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Nome *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="equip-name" /></div>
            <div className="space-y-1.5"><Label>Categoria</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Codice Interno</Label><Input value={form.internal_code} onChange={(e) => setForm({ ...form, internal_code: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Seriale</Label><Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Targa (opzionale)</Label><Input value={form.license_plate} onChange={(e) => setForm({ ...form, license_plate: e.target.value })} placeholder="Es. AB123CD" data-testid="equip-license-plate" /></div>
            <div className="space-y-1.5"><Label>Marca</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Modello</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Data Acquisto</Label><Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Posizione</Label><Input value={form.current_location} onChange={(e) => setForm({ ...form, current_location: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
            <Button onClick={handleCreate} disabled={!form.name} data-testid="equip-save-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Maintenance Dialog */}
      <Dialog open={showMaint} onOpenChange={setShowMaint}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuova Manutenzione</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Attrezzatura *</Label>
              <Select value={maintForm.equipment_id} onValueChange={(v) => setMaintForm({ ...maintForm, equipment_id: v })}>
                <SelectTrigger data-testid="maint-equip-select"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>{items.map(eq => <SelectItem key={eq.id} value={eq.id}>{eq.name} ({eq.internal_code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Data *</Label><Input type="date" value={maintForm.maintenance_date} onChange={(e) => setMaintForm({ ...maintForm, maintenance_date: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={maintForm.maintenance_type} onValueChange={(v) => setMaintForm({ ...maintForm, maintenance_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ordinaria">Ordinaria</SelectItem><SelectItem value="straordinaria">Straordinaria</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Descrizione</Label><Input value={maintForm.description} onChange={(e) => setMaintForm({ ...maintForm, description: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Ricambi</Label><Input value={maintForm.spare_parts} onChange={(e) => setMaintForm({ ...maintForm, spare_parts: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Costo (EUR)</Label><Input type="number" value={maintForm.cost} onChange={(e) => setMaintForm({ ...maintForm, cost: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Fornitore</Label><Input value={maintForm.supplier} onChange={(e) => setMaintForm({ ...maintForm, supplier: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Tecnico</Label><Input value={maintForm.technician} onChange={(e) => setMaintForm({ ...maintForm, technician: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Prossima Scadenza</Label><Input type="date" value={maintForm.next_due_date} onChange={(e) => setMaintForm({ ...maintForm, next_due_date: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMaint(false)}>Annulla</Button>
            <Button onClick={handleMaint} disabled={!maintForm.equipment_id || !maintForm.maintenance_date} data-testid="maint-save-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Equipment Document Dialog */}
      <Dialog open={showDocCreate} onOpenChange={setShowDocCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuovo Documento Attrezzatura</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Attrezzatura *</Label>
              <Select value={docForm.equipment_id} onValueChange={(v) => setDocForm({ ...docForm, equipment_id: v })}>
                <SelectTrigger data-testid="doc-equip-select"><SelectValue placeholder="Seleziona attrezzatura..." /></SelectTrigger>
                <SelectContent>{items.map(eq => <SelectItem key={eq.id} value={eq.id}>{eq.name} ({eq.internal_code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Titolo *</Label><Input value={docForm.title} onChange={(e) => setDocForm({ ...docForm, title: e.target.value })} placeholder="es. Assicurazione 2026" data-testid="doc-title" /></div>
            <div className="space-y-1.5">
              <Label>Tipo Documento</Label>
              <Select value={docForm.doc_type} onValueChange={(v) => setDocForm({ ...docForm, doc_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map(dt => <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Riferimento/Numero</Label><Input value={docForm.reference_number} onChange={(e) => setDocForm({ ...docForm, reference_number: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Ente Emittente</Label><Input value={docForm.issuing_entity} onChange={(e) => setDocForm({ ...docForm, issuing_entity: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Data Emissione</Label><Input type="date" value={docForm.issue_date} onChange={(e) => setDocForm({ ...docForm, issue_date: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Data Scadenza</Label><Input type="date" value={docForm.expiry_date} onChange={(e) => setDocForm({ ...docForm, expiry_date: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Note</Label><Input value={docForm.notes} onChange={(e) => setDocForm({ ...docForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocCreate(false)}>Annulla</Button>
            <Button onClick={handleDocCreate} disabled={!docForm.equipment_id || !docForm.title} data-testid="doc-save-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Equipment Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifica Attrezzatura</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Nome *</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Categoria</Label><Input value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Codice Interno</Label><Input value={editForm.internal_code} onChange={(e) => setEditForm({ ...editForm, internal_code: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Seriale</Label><Input value={editForm.serial_number} onChange={(e) => setEditForm({ ...editForm, serial_number: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Targa (opzionale)</Label><Input value={editForm.license_plate} onChange={(e) => setEditForm({ ...editForm, license_plate: e.target.value })} placeholder="Es. AB123CD" /></div>
            <div className="space-y-1.5"><Label>Marca</Label><Input value={editForm.brand} onChange={(e) => setEditForm({ ...editForm, brand: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Modello</Label><Input value={editForm.model} onChange={(e) => setEditForm({ ...editForm, model: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Stato</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="disponibile">Disponibile</SelectItem>
                  <SelectItem value="assegnata">Assegnata</SelectItem>
                  <SelectItem value="in_manutenzione">In Manutenzione</SelectItem>
                  <SelectItem value="non_disponibile">Non Disponibile</SelectItem>
                  <SelectItem value="fuori_uso">Fuori Uso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Posizione</Label><Input value={editForm.current_location} onChange={(e) => setEditForm({ ...editForm, current_location: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Note</Label><Input value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Annulla</Button>
            <Button onClick={handleSaveEdit} disabled={!editForm.name}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Equipment Dialog */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Elimina Attrezzatura</DialogTitle>
            <DialogDescription>Scegli come procedere con {selectedEquipment?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <Button variant="outline" className="w-full justify-start text-amber-600 hover:text-amber-700 hover:bg-amber-50" onClick={() => confirmDelete(false)}>
              <Wrench size={16} className="mr-2" /> Segna come Fuori Uso
            </Button>
            <Button variant="outline" className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => confirmDelete(true)}>
              <Trash2 size={16} className="mr-2" /> Elimina definitivamente
            </Button>
            <p className="text-xs text-slate-500 mt-2">L'eliminazione definitiva rimuovera anche documenti, storico e manutenzioni.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDelete(false)}>Annulla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={!!showUpload} onOpenChange={() => setShowUpload(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Carica File</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <Input 
              type="file" 
              ref={fileInputRef}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xls,.xlsx"
              onChange={(e) => {
                if (e.target.files?.[0] && showUpload) {
                  handleUploadDoc(showUpload, e.target.files[0]);
                }
              }}
            />
            <p className="text-xs text-slate-500">Formati accettati: PDF, DOC, DOCX, immagini, Excel</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowUpload(null)}>Annulla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
